/**
 * usePracticeNavigation.ts — Extracted from PracticeView.vue Phase 3
 *
 * Contains sentence navigation (advanceToSentence, handleSentenceClick),
 * segment interaction (handleSegmentClick, handleSegmentBoundaryClick),
 * overlay management (closePracticeOverlay, closePopover),
 * recording queries (hasRecordingBySentenceIndex, hasRecordingByAnnotatedSentence),
 * and audio refetching (refetchAudio).
 * This is a purely mechanical extraction with zero logic changes.
 *
 * State remains in PracticeView's reactive object; this composable operates
 * on Ref-wrapped state properties passed via the deps interface.
 */
import type { SentenceRecordingType, SentenceType } from 'types/types'
import { computed, ComputedRef, isRef, nextTick, Ref, ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import {
  MIN_PITCH,
  RESET_TO_SENTENCE,
  RESET_TO_WORDS,
  UI_MAX_PITCH_SHIFT,
  UI_MIN_PITCH_SHIFT,
  uiPitchToActual,
} from '@/constants/practiceConstants'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import type { PitchShiftType, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { segmentationFromRecording } from '@/util/RecordingHelpers'
import type { VoiceStyleSet } from '@/util/VoiceStyles'
import { inReadMode, inTextMode } from '@/util/VoiceStyles'

// Re-export pitch shift constants for backwards compatibility with existing imports
export { MIN_PITCH, UI_MAX_PITCH_SHIFT, UI_MIN_PITCH_SHIFT }

// {{{ interface
export interface PracticeNavigationDeps {
  // State refs (from PracticeView's toRefs — Navigation reads and writes these)
  annotatedSentences: Ref<Array<AnnotatedSentence>>
  downloading: Ref<boolean>
  focusAnnotatedSentence: Ref<AnnotatedSentence | null>
  focusSentenceIndex: Ref<number | null>
  focusSentenceRecordingMetadataWithPitchedAudios: Ref<SentenceRecordingMetadataWithPitchedAudios | null>
  fsmHistory: Ref<string[]>
  fsmState: Ref<number>
  pitchShift: Ref<PitchShiftType>
  popoverSegment: Ref<AnnotatedSegment | null>
  popoverSegmentIndex: Ref<number | null>
  popoverSentence: Ref<AnnotatedSentence | null>
  popoverSentenceIndex: Ref<number | null>
  popoverVisible: Ref<boolean>
  segmentClicked: Ref<number | null>
  segmentation: Ref<Array<number> | null>
  uiPitchShift: Ref<number>
  voiceStyleSet: Ref<VoiceStyleSet>

  // State refs needed for surround computeds
  sentences: Ref<Array<SentenceType>>
  surroundLines: Ref<number>

  // Computed refs (from PracticeView)
  isRecording: ComputedRef<boolean>
  isSegmented: ComputedRef<boolean>
  recordingsInStyle: ComputedRef<Record<string, SentenceRecordingMetadataWithPitchedAudios[]>>
  sentence: ComputedRef<SentenceType | null>

  // From useFollower
  setCurrentSegment: (sentenceIdx: number, segmentIdx: number) => void

  // From useAudioPlayer
  pause: () => void
  playing: Ref<boolean>
  purge: () => void
  setLoadPlay: (payload: {
    pitchShift: PitchShiftType
    segmentIndex?: number
    sentence?: SentenceType
    sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios
  }) => Promise<void>

  // From useSegmentation (late-bound via setResetSegments)
  // resetSegments is wired after construction because useSegmentation depends on surroundBeforeSentences

  // Callbacks (defined in PracticeView, hoisted functions)
  pushFocusPoint: () => void

  // Store
  store: {
    dispatch: {
      audio: {
        fetchAudio: (params: { cache: boolean; pitchShift: PitchShiftType; sentenceRecording: SentenceRecordingType }) => Promise<void>
      }
    }
  }
}
// }}}

export function usePracticeNavigation(logger: VueLogger, deps: PracticeNavigationDeps) {
  // {{{ late-bound functions (circular dependency resolution)
  // closePracticeOverlay needs session.toggleRecord; handleSegmentClick needs fsm.driveState.
  // These composables are constructed after navigation, so we late-bind them.
  let _toggleRecord: () => void = () => {
    logger.error('toggleRecord called before wired')
  }
  let _driveState: () => void = () => {
    logger.error('driveState called before wired')
  }

  function setToggleRecord(fn: () => void) {
    _toggleRecord = fn
  }
  function setDriveState(fn: () => void) {
    _driveState = fn
  }

  let _resetSegments: (mode: number, annotatedSentence: Ref<AnnotatedSentence>) => void = () => {
    logger.error('resetSegments called before wired')
  }
  function setResetSegments(fn: (mode: number, annotatedSentence: Ref<AnnotatedSentence>) => void) {
    _resetSegments = fn
  }
  // }}}

  // {{{ surround computeds
  /**
   * @invariant Returns a non-negative index into deps.sentences
   * @depends-on deps.focusSentenceIndex, deps.surroundLines
   */
  const surroundBeforeStartIndex = computed<number>(() => {
    if (deps.focusSentenceIndex.value === null) {
      logger.error('unexpected null state.focusSentenceIndex')
      return 0
    }
    return Math.max(0, deps.focusSentenceIndex.value - deps.surroundLines.value)
  })

  /**
   * @invariant Returns sentences before the focus sentence for surround context display
   * @depends-on deps.focusSentenceIndex, surroundBeforeStartIndex, deps.sentences
   */
  const surroundBeforeSentences = computed<Array<SentenceType>>(() => {
    if (deps.focusSentenceIndex.value === null) {
      logger.error('unexpected null state.focusSentenceIndex')
      return []
    }
    const endSlice = Math.max(0, deps.focusSentenceIndex.value)
    return deps.sentences.value.slice(surroundBeforeStartIndex.value, endSlice)
  })

  /**
   * @invariant Returns sentences after the focus sentence for surround context display
   * @depends-on deps.focusSentenceIndex, deps.annotatedSentences, deps.surroundLines, deps.sentences
   */
  const surroundAfterSentences = computed<Array<SentenceType>>(() => {
    if (deps.focusSentenceIndex.value === null) {
      logger.error('unexpected null state.focusSentenceIndex')
      return []
    }
    const startSlice = deps.focusSentenceIndex.value + 1
    if (startSlice >= deps.annotatedSentences.value.length) {
      return []
    }
    const endSlice = Math.min(deps.annotatedSentences.value.length - 1, deps.focusSentenceIndex.value + deps.surroundLines.value)
    return deps.sentences.value.slice(startSlice, endSlice + 1)
  })

  // {{{ hasRecordingByAnnotatedSentence
  /**
   * @postcondition Returns true iff not in text mode AND sentence has a recording in recordingsInStyle
   * @depends-on deps.voiceStyleSet, deps.recordingsInStyle
   */
  function hasRecordingByAnnotatedSentence(annotatedSentence: AnnotatedSentence | null): boolean {
    // do not involve state.downloading as there is a race condition between
    // clicking on a sentence to practice it and the audio pre-fetching that
    // occurs
    if (!annotatedSentence) {
      logger.error('unexpected null annotatedSentence in hasRecordingByAnnotatedSentence')
      return false
    }

    return !inTextMode(deps.voiceStyleSet.value) && annotatedSentence.sentence().id in deps.recordingsInStyle.value
  }
  // }}}

  // {{{ hasRecordingBySentenceIndex
  /**
   * @precondition deps.annotatedSentences is populated
   * @postcondition Returns true iff the sentence at sentenceIndex has a recording in the current voice style
   * @depends-on deps.annotatedSentences, deps.recordingsInStyle, deps.voiceStyleSet
   */
  function hasRecordingBySentenceIndex(sentenceIndex: number): boolean {
    if (!deps.annotatedSentences.value) {
      logger.error('unexpected null state.annotatedSentences in hasRecordingByAnnotatedSentence')
      return false
    }

    if (sentenceIndex < 0 || sentenceIndex >= deps.annotatedSentences.value.length) {
      return false
    }

    const annotatedSentence = deps.annotatedSentences.value[sentenceIndex]
    return hasRecordingByAnnotatedSentence(annotatedSentence as AnnotatedSentence)
  }
  // }}}

  // {{{ closePopover
  /**
   * @postcondition Popover hidden; audio purged; popoverSegment cleared
   * @sideeffect Calls purge() on audioPlayer; mutates popover state
   */
  function closePopover() {
    deps.purge()
    deps.popoverSegment.value = null
    deps.popoverVisible.value = false
  }
  // }}}

  // {{{ closePracticeOverlay
  /**
   * @postcondition Practice overlay closed; all focus/popover/segmentation state cleared;
   *   recording stopped if active; audio paused if playing
   * @sideeffect Calls toggleRecord() if recording; calls pause() if playing;
   *   clears focusAnnotatedSentence, focusSentenceIndex, focusSentenceRecordingMetadataWithPitchedAudios,
   *   segmentation, popover state
   */
  function closePracticeOverlay(): void {
    if (deps.isRecording.value) {
      _toggleRecord()
    }

    deps.focusAnnotatedSentence.value = null
    deps.focusSentenceIndex.value = null
    deps.focusSentenceRecordingMetadataWithPitchedAudios.value = null
    deps.segmentation.value = null
    if (deps.playing.value) {
      deps.pause()
    }
    deps.popoverSegment.value = null
    deps.popoverSegmentIndex.value = null
    deps.popoverSentenceIndex.value = null
    deps.popoverSentence.value = null
    deps.popoverVisible.value = false
  }
  // }}}

  // {{{ handleSentenceClick
  /**
   * @precondition annotatedSentence has a recording in recordingsInStyle; sentenceIndex is not null
   * @postcondition Practice overlay focused on the given sentence; audio fetched and ready to play;
   *   segmentation set from recording metadata or reset to words/sentence
   * @sideeffect Sets focusSentenceIndex, focusAnnotatedSentence, focusSentenceRecordingMetadataWithPitchedAudios,
   *   segmentation; calls store.dispatch.audio.fetchAudio; prefetches next sentence audio
   * @sideeffect Pauses any playing audio; sets downloading=true then false
   * @async-note Returns Promise that resolves after audio fetch completes
   */
  function handleSentenceClick(annotatedSentence: AnnotatedSentence, sentenceIndex: number | null): Promise<void> {
    return new Promise((resolve) => {
      logger.info('handleSentenceClick')
      deps.fsmHistory.value.push('SenC')
      if (!hasRecordingByAnnotatedSentence(annotatedSentence)) {
        return resolve()
      }

      if (sentenceIndex === null) {
        logger.error('unexpected null sentenceIndex in handleSentenceClick')
        return resolve()
      }

      // to remember which sentence out of all sentences we're focusing
      // on, use focusSentenceIndex.
      deps.focusSentenceIndex.value = sentenceIndex
      deps.focusAnnotatedSentence.value = deps.annotatedSentences.value[sentenceIndex]

      if (!deps.sentence.value) {
        logger.error('unexpected null sentence.value in handleSentenceClick')
        return resolve()
      }

      deps.focusSentenceRecordingMetadataWithPitchedAudios.value = deps.recordingsInStyle.value[deps.sentence.value.id][0]
      logger.debug('handleSentenceClick, state.focusSentenceRecordingMetadataWithPitchedAudios =')
      logger.debug(deps.focusSentenceRecordingMetadataWithPitchedAudios.value)

      if (!deps.focusSentenceRecordingMetadataWithPitchedAudios.value) {
        logger.debug('unexpected null state.focusSentenceRecordingMetadataWithPitchedAudios')
        return resolve()
      }

      const refAnnotatedSentence: Ref<AnnotatedSentence> = isRef(annotatedSentence)
        ? (annotatedSentence as Ref<AnnotatedSentence>)
        : (ref(annotatedSentence) as Ref<AnnotatedSentence>)

      // set default segmentation
      if (!deps.isSegmented.value) {
        logger.debug('handleSentenceClick, not segmented')
        _resetSegments(inReadMode(deps.voiceStyleSet.value) ? RESET_TO_WORDS : RESET_TO_SENTENCE, refAnnotatedSentence)
      } else if (!deps.focusSentenceRecordingMetadataWithPitchedAudios.value.sentenceRecording?.segmentedSentence) {
        logger.debug('handleSentenceClick, segmented, but null sentenceRecording.segmentedSentence')
        logger.error('unexpected null state.focusSentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.segmentedSentence')
        return resolve()
      } else {
        logger.debug('handleSentenceClick, looping over segments')
        deps.segmentation.value = segmentationFromRecording(deps.focusSentenceRecordingMetadataWithPitchedAudios.value)
      }

      // if anything was playing, shut it up
      if (deps.playing.value) {
        deps.pause()
      }

      // currentSentenceIndex is being used the followerMixin to highlight segments as
      // they are being read during practice.
      deps.setCurrentSegment(surroundBeforeSentences.value.length, -1)

      // loads all audio files from the backend and then kicks the annotated-text component
      // because it may decide to render differently depending on whether certain segments
      // have an available audio or not.
      deps.downloading.value = true
      logger.debug('handleSentenceClick, calling fetchAudio')
      deps.store.dispatch.audio
        .fetchAudio({
          cache: true,
          pitchShift: deps.pitchShift.value,
          sentenceRecording: deps.focusSentenceRecordingMetadataWithPitchedAudios.value.sentenceRecording as SentenceRecordingType,
        })
        .then(() => {
          deps.downloading.value = false
          resolve()
        })
        .catch((error) => {
          deps.downloading.value = false
          logger.error('handleSentenceClick fetchAudio failed:', error)
          resolve()
        })

      // prefetch the next sentence, for smooth transition in case of auto-advance
      const nextIndex = sentenceIndex + 1
      if (nextIndex < deps.annotatedSentences.value.length) {
        const nextAnnotatedSentence = deps.annotatedSentences.value[nextIndex]
        if (deps.recordingsInStyle.value[nextAnnotatedSentence.sentence().id]) {
          const [nextSentenceRecordingMetadataWithPitchedAudios] = deps.recordingsInStyle.value[nextAnnotatedSentence.sentence().id]
          deps.store.dispatch.audio
            .fetchAudio({
              cache: true,
              pitchShift: deps.pitchShift.value,
              sentenceRecording: nextSentenceRecordingMetadataWithPitchedAudios.sentenceRecording as SentenceRecordingType,
            })
            .catch(() => {}) // prefetch failure is non-critical
        }
      }

      // // Scroll to the practice overlay on the nextTick, as a hidden component
      // // won't reactively respond.
      // state.$nextTick(() => {
      //   state.$refs.practiceSpan.scrollIntoView({
      //     behavior: 'smooth',
      //     block: 'center',
      //     inline: 'nearest',
      //   });
      // });
    })
  }
  // }}}

  // {{{ advanceToSentence
  /**
   * @precondition deps.focusSentenceIndex is not null; target sentence has a recording
   * @postcondition Focus moved to new sentence; if recording, pushFocusPoint called
   * @sideeffect Closes popover; calls handleSentenceClick for target sentence
   * @async-note Returns Promise that resolves after handleSentenceClick (audio fetch) completes
   */
  function advanceToSentence(increment: number, absolute = false): Promise<void> {
    deps.popoverVisible.value = false
    return new Promise((resolve) => {
      if (deps.focusSentenceIndex.value === null) {
        logger.error('unexpected null state.focusSentenceIndex')
        return resolve()
      }

      const nextSentenceIdx = absolute ? increment : deps.focusSentenceIndex.value + increment
      const nextSentence = deps.annotatedSentences.value[nextSentenceIdx] as AnnotatedSentence
      handleSentenceClick(nextSentence, nextSentenceIdx).then(() => {
        if (deps.isRecording.value) {
          deps.pushFocusPoint()
        }
        resolve()
      })
    })
  }
  // }}}

  // {{{ handleSegmentBoundaryClick
  /**
   * @postcondition If clicked sentence differs from focus sentence: focus switches, returns true.
   *   If recording or same sentence: no action, returns false.
   * @sideeffect May call handleSentenceClick to switch focus sentence
   */
  function handleSegmentBoundaryClick(
    annotatedSentence: AnnotatedSentence,
    __: AnnotatedSegment,
    sentenceIndex: number,
    ___: number,
  ): boolean {
    if (deps.isRecording.value) {
      return false
    }

    // if clicked on a sentence that isn't the currently focus sentence, then switch
    const absoluteSentenceIndex = surroundBeforeStartIndex.value + sentenceIndex
    if (absoluteSentenceIndex !== deps.focusSentenceIndex.value) {
      handleSentenceClick(annotatedSentence, absoluteSentenceIndex)
      return true
    }
    return false
  }
  // }}}

  // {{{ handleSegmentClick
  /**
   * @postcondition When not recording: opens popover on clicked segment and plays segment audio.
   *   When recording: sets segmentClicked and drives FSM to state 7 (user-initiated segment jump).
   * @sideeffect Stops any playing audio; may open/close popover; calls setLoadPlay or drives FSM
   * @async-note Popover uses double-nextTick to close then reopen (avoids Vuetify positioning bug)
   */
  function handleSegmentClick(
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ): void {
    logger.info('handleSegmentClick')
    deps.fsmHistory.value.push('SegC')

    // can't click during practice on a downloading audio segment
    if (deps.downloading.value) {
      return
    }

    // if audio is playing, stop it
    if (deps.playing.value) {
      deps.pause()
    }

    // if the user clicked to refocus sentence based on surroundLines, then we're done
    if (handleSegmentBoundaryClick(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)) {
      return
    }

    if (!deps.isRecording.value) {
      if (annotatedSegment !== deps.popoverSegment.value) {
        // close popover if another segment was clicked
        if (deps.popoverVisible.value) {
          deps.popoverVisible.value = false
          deps.popoverSegment.value = null
        }
        nextTick(() => {
          nextTick(() => {
            deps.popoverSentence.value = annotatedSentence
            deps.popoverSegment.value = annotatedSegment
            deps.popoverSentenceIndex.value = sentenceIndex
            deps.popoverSegmentIndex.value = segmentIndex
            deps.popoverVisible.value = true
          })
        })
      }

      const paramsToPlaySentenceOrSegmentAudio = {
        pitchShift: deps.pitchShift.value,
        segmentIndex,
        sentence: annotatedSentence.sentence(),
        sentenceRecordingMetadataWithPitchedAudios: deps.recordingsInStyle.value[annotatedSentence.sentence().id][0],
      }
      logger.debug('calling pauseCurrentSegmentOrPlayOtherSegment with paramsToPlaySentenceOrSegmentAudio =')
      logger.debug(paramsToPlaySentenceOrSegmentAudio)
      deps.setLoadPlay(paramsToPlaySentenceOrSegmentAudio).catch((err: Error) => {
        logger.error('unexpected catch in handleSegmentClick')
        console.error('handleSegmentClick catch:', err?.message, err?.stack)
      })
      return
    }

    deps.segmentClicked.value = segmentIndex
    deps.fsmState.value = 7
    _driveState()
  }
  // }}}

  // {{{ refetchAudio
  /**
   * @precondition focusSentenceRecordingMetadataWithPitchedAudios and focusAnnotatedSentence are set
   * @postcondition Audio re-fetched at new pitch shift; downloading flag managed
   * @sideeffect Computes pitchShift from uiPitchShift; calls store.dispatch.audio.fetchAudio;
   *   sets downloading=true then false
   */
  function refetchAudio() {
    deps.pitchShift.value = uiPitchToActual(deps.uiPitchShift.value)

    if (!deps.focusSentenceRecordingMetadataWithPitchedAudios.value) {
      logger.info('null state.focusSentenceRecordingMetadataWithPitchedAudios')
      return
    }

    if (!deps.focusSentenceRecordingMetadataWithPitchedAudios.value.sentenceRecording) {
      logger.info('null state.focusSentenceRecordingMetadataWithPitchedAudios')
      return
    }

    if (!deps.focusAnnotatedSentence.value) {
      logger.info('null state.focusAnnotatedSentence')
      return
    }

    deps.downloading.value = true
    deps.store.dispatch.audio
      .fetchAudio({
        cache: true,
        pitchShift: deps.pitchShift.value,
        sentenceRecording: deps.focusSentenceRecordingMetadataWithPitchedAudios.value.sentenceRecording,
      })
      .then(() => {
        deps.downloading.value = false
      })
      .catch((error) => {
        deps.downloading.value = false
        logger.error('refetchAudio failed:', error)
      })
  }
  // }}}

  return {
    advanceToSentence,
    closePopover,
    closePracticeOverlay,
    handleSegmentBoundaryClick,
    handleSegmentClick,
    handleSentenceClick,
    hasRecordingByAnnotatedSentence,
    hasRecordingBySentenceIndex,
    refetchAudio,
    setDriveState,
    setResetSegments,
    setToggleRecord,
    surroundAfterSentences,
    surroundBeforeSentences,
    surroundBeforeStartIndex,
  }
}
